import {
	Button,
	ClassNameProps,
	Column,
	makeClassName,
	Row,
	StyleProps,
	toGrammaticalNumber,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	Address,
	AddressInformationFragment,
	EmailAddress,
	Guardian,
	Person,
	PersonNameAndImageFragment,
	PhoneNumber,
	Relationship,
} from "../../../../Utilities";
import {
	AddressFormat,
	EmailAddressFormat,
	GuardianFormat,
	PhoneNumberFormat,
	RelationshipFormat,
} from "../../../Format";
import { GuardianIcon } from "../Icon";
import styles from "./styles.module.css";

export type ContactInformationReviewCardGuardian = Pick<Guardian, "id"> & {
	person: Pick<Person, "id"> &
		PersonNameAndImageFragment & {
			phoneNumbers: Pick<PhoneNumber, "id" | "phoneNumber">[];
			emailAddresses: Pick<EmailAddress, "id" | "emailAddress">[];
			addresses: (Pick<Address, "id"> & AddressInformationFragment)[];
		};
	relationships: Pick<Relationship, "id" | "relationshipType">[];
};

export type GuardianContactInformationReviewCardProps = {
	guardian: ContactInformationReviewCardGuardian;
	onEditClick?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function GuardianContactInformationReviewCard({
	guardian,
	onEditClick,
	className,
	style,
}: GuardianContactInformationReviewCardProps) {
	return (
		<Column
			justify="spaced-start"
			verticalSpacing="1rem"
			className={makeClassName(styles["card"], className)}
			style={style}
		>
			<Row justify="spaced-start" horizontalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ flexGrow: 1, minWidth: 0 }}>
					<GuardianIcon guardian={guardian} size={64} />

					<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
						<TruncatedText className={styles["card__name"]} style={{ minWidth: 0 }}>
							{GuardianFormat.name(guardian, { ignoreUser: true, ignorePublicName: true })}
						</TruncatedText>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
							<Icon.PersonArmsSpread size={16} color="var(--neutral-colors--tertiary)" />
							<TruncatedText className={styles["card__detail__text"]}>
								{RelationshipFormat.condensedRelationshipType(guardian.relationships)}
							</TruncatedText>
						</Row>
					</Column>
				</Row>

				{onEditClick && (
					<Button variant="link" size="small" onClick={onEditClick} className="button--icon">
						<Icon.PencilSimple size={24} className="block" />
					</Button>
				)}
			</Row>

			<Column>
				<span className={styles["card__label"]}>
					Phone {toGrammaticalNumber("Number", guardian.person.phoneNumbers.length)}
				</span>

				{guardian.person.phoneNumbers.map((phoneNumber) => (
					<Row key={phoneNumber.id} justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<Icon.Phone size={16} color="var(--neutral-colors--tertiary)" />
						<TruncatedText className={styles["card__detail__text"]}>
							{PhoneNumberFormat.Fields.phoneNumber(phoneNumber.phoneNumber)}
						</TruncatedText>
					</Row>
				))}
			</Column>

			<Column>
				<span className={styles["card__label"]}>
					Email {toGrammaticalNumber("Address", guardian.person.phoneNumbers.length)}
				</span>

				{guardian.person.emailAddresses.map((emailAddress) => (
					<Row key={emailAddress.id} justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<Icon.EnvelopeSimple size={16} color="var(--neutral-colors--tertiary)" />
						<TruncatedText className={styles["card__detail__text"]}>
							{EmailAddressFormat.Fields.emailAddress(emailAddress.emailAddress)}
						</TruncatedText>
					</Row>
				))}
			</Column>

			<Column>
				<span className={styles["card__label"]}>
					{toGrammaticalNumber("Address", guardian.person.addresses.length)}
				</span>

				{guardian.person.addresses.map((address) => (
					<Row key={address.id} justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<Icon.House size={16} color="var(--neutral-colors--tertiary)" />
						<TruncatedText className={styles["card__detail__text"]}>{AddressFormat.information(address)}</TruncatedText>
					</Row>
				))}
			</Column>
		</Column>
	);
}
